/**
 * Loader for the static RUM configuration emitted into the SPA bucket at deploy
 * time (#3019). The UI CDK stack writes `/rum-config.json` containing the RUM
 * app-monitor id, the Cognito identity-pool id, and the region (sourced from the
 * three SSM params provisioned by #3018). The SPA fetches it same-origin (it is
 * covered by `connect-src 'self'`) at the very top of bootstrap, before
 * `initRum` and before `configureAmplify`, so RUM is up in time to capture the
 * tenant-config fetch failure (the #3016 case).
 *
 * A missing, unreachable, or malformed config resolves to `null`. The loader
 * NEVER throws — `initRum(null)` no-ops and the app boots normally.
 */

/** Resolved RUM configuration needed to construct the AwsRum client. */
export interface RumConfig {
    /** CloudWatch RUM app-monitor id. */
    appMonitorId: string;
    /** Cognito identity-pool id backing the (unauthenticated) RUM client. */
    identityPoolId: string;
    /** Region the RUM app monitor lives in. */
    region: string;
}

/** Narrows an unknown parsed body to a non-empty RumConfig, or returns null. */
function parseRumConfig(data: unknown): RumConfig | null {
    if (data === null || typeof data !== 'object') {
        return null;
    }
    const { appMonitorId, identityPoolId, region } = data as Record<string, unknown>;
    if (
        typeof appMonitorId !== 'string' ||
        appMonitorId.length === 0 ||
        typeof identityPoolId !== 'string' ||
        identityPoolId.length === 0 ||
        typeof region !== 'string' ||
        region.length === 0
    ) {
        return null;
    }
    return { appMonitorId, identityPoolId, region };
}

/**
 * Fetches and validates `/rum-config.json`. Same-origin, never cached.
 *
 * @returns The parsed {@link RumConfig}, or `null` if the config is missing,
 *   unreachable, or malformed. This function never rejects or throws.
 */
export async function fetchRumConfig(): Promise<RumConfig | null> {
    try {
        const response = await fetch('/rum-config.json', { cache: 'no-store' });
        if (!response.ok) {
            return null;
        }
        const data: unknown = await response.json();
        return parseRumConfig(data);
    } catch {
        return null;
    }
}
